import { z } from 'zod/v4';

import { CurrencyRuntype } from '../backendTypes';

// Runtypes for the Stackrate price service (scala-workspace/framework/price).
// They mirror the Lagom JSON wire shapes from the Scala sources:
//   framework/price/api/.../Pricing.scala        (Pricing, DecisionTable, VersionedDecisionTable, ...)
//   framework/price/api/.../package.scala        (PriceRequest, DeployedPricing, PricingVariable, ...)
//   framework/price/api/.../CapabilitiesApi.scala (CapabilityCheckResult, ...)
// Scala `Option` fields are omitted when None, hence `.optional()`.
// Scala `Instant` fields serialize as ISO-8601 UTC strings (play-json default), hence `z.string()`.

export const PcbSupplierReferenceRuntype = z
    .string()
    .describe(
        'Supplier reference — pass the luminovo supplier-and-stock-location ID (preferred; see GET /suppliers-and-stock-locations). The canonical stackrate PCB supplier ID or its lqReference are also accepted; the backend normalizes, auto-creating the pcb supplier on writes.',
    );

export const StackrateSupplierIdRuntype = z
    .string()
    .describe(
        'Canonical stackrate PCB supplier ID (ems/pcb-supplier service) — not a luminovo ID; join back to luminovo via supplierLqReference (when its kind is supplier_and_stock_location)',
    );

// The `kind` tag values are a wire contract shared with the Scala LuminovoSupplierReference and
// luminovo's classify endpoint — never rename them independently.
const LuminovoSupplierReferenceRuntype = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('supplier_and_stock_location'), id: z.string() }),
    z.object({ kind: z.literal('supplier'), id: z.string() }),
]);

const SupplierLqReferenceRuntype = LuminovoSupplierReferenceRuntype.optional().describe(
    'The luminovo reference linked to this supplier, tagged with its id-space: kind "supplier_and_stock_location" carries a supplier-and-stock-location ID (the usual case, and the key for joining back to luminovo); kind "supplier" carries a bare luminovo supplier ID found in legacy data. Omitted when the supplier has no reference, when the reference is not classified yet, or when supplier resolution was temporarily unavailable. Table-mutation responses (add/delete table) always omit it.',
);

export const DecisionTableInputRuntype = z.object({
    label: z.string(),
    name: z.string(),
    varType: z.string(),
});
export type DecisionTableInput = z.infer<typeof DecisionTableInputRuntype>;

export const DecisionTableOutputRuntype = z.object({
    label: z.string(),
    name: z.string(),
    varType: z.string(),
});
export type DecisionTableOutput = z.infer<typeof DecisionTableOutputRuntype>;

export const DecisionTableRuleRuntype = z.object({
    inputEntries: z.array(z.object({ name: z.string(), value: z.string() })),
    outputEntries: z.array(z.object({ name: z.string(), value: z.string() })),
});
export type DecisionTableRule = z.infer<typeof DecisionTableRuleRuntype>;

export const DecisionTableRuntype = z.object({
    team: z.string().optional(),
    name: z.string(),
    inputs: z.array(DecisionTableInputRuntype),
    outputs: z.array(DecisionTableOutputRuntype),
    rules: z.array(DecisionTableRuleRuntype),
    hitpolicy: z.string().optional(),
});
export type DecisionTable = z.infer<typeof DecisionTableRuntype>;

export const DecisionTableVersionRuntype = z.object({
    table: DecisionTableRuntype,
    created: z.string(),
    creator: z.string(),
});
export type DecisionTableVersion = z.infer<typeof DecisionTableVersionRuntype>;

export const VersionedDecisionTableRuntype = z.object({
    team: z.string(),
    currentVersion: z.number(),
    name: z.string(),
    id: z.string(),
    versions: z.array(DecisionTableVersionRuntype),
});
export type VersionedDecisionTable = z.infer<typeof VersionedDecisionTableRuntype>;

export const PricingRuntype = z.object({
    name: z.string(),
    id: z.string().optional(),
    supplier: StackrateSupplierIdRuntype,
    tables: z.array(VersionedDecisionTableRuntype),
    enableApi: z.boolean(),
    currency: CurrencyRuntype.optional(),
    supplierLqReference: SupplierLqReferenceRuntype,
});
export type Pricing = z.infer<typeof PricingRuntype>;

// A pricing's deployed decision-table reference (team + table key).
export const DeployedDecisionRuntype = z.object({
    team: z.string(),
    key: z.string(),
});
export type DeployedDecision = z.infer<typeof DeployedDecisionRuntype>;

export const DeployedPricingRuntype = z.object({
    name: z.string(),
    id: z.string(),
    supplier: StackrateSupplierIdRuntype,
    pricings: z.array(DeployedDecisionRuntype),
    enableApi: z.boolean(),
    currency: CurrencyRuntype.optional(),
    supplierLqReference: SupplierLqReferenceRuntype,
});
export type DeployedPricing = z.infer<typeof DeployedPricingRuntype>;

// Request body of PUT /price/calculation/:nameOrID (change an existing pricing).
export const PricingDescriptorRuntype = z.object({
    name: z.string(),
    supplier: PcbSupplierReferenceRuntype,
    id: z.string().optional(),
    tables: z.array(DeployedDecisionRuntype).optional(),
    enableApi: z.boolean().optional(),
    currency: CurrencyRuntype.optional(),
});
export type PricingDescriptor = z.infer<typeof PricingDescriptorRuntype>;

export const PricingDeploymentDescriptorRuntype = z.object({
    name: z.string(),
    id: z.string().optional(),
    supplier: PcbSupplierReferenceRuntype,
    enableApi: z.boolean().optional(),
    currency: CurrencyRuntype.optional(),
    tables: z.array(DecisionTableRuntype),
});
export type PricingDeploymentDescriptor = z.infer<typeof PricingDeploymentDescriptorRuntype>;

// Re-POSTable to POST /price/calculation, which ignores the extra field; `supplier` is overridden
// to read-side semantics (exports carry the canonical stackrate ID).
export const PricingExportRuntype = PricingDeploymentDescriptorRuntype.extend({
    supplier: StackrateSupplierIdRuntype,
    supplierLqReference: SupplierLqReferenceRuntype,
});
export type PricingExport = z.infer<typeof PricingExportRuntype>;

export const CapabilitiesRuntype = z.object({
    supplier: StackrateSupplierIdRuntype,
    tables: z.array(VersionedDecisionTableRuntype),
    supplierLqReference: SupplierLqReferenceRuntype,
});
export type Capabilities = z.infer<typeof CapabilitiesRuntype>;

export const DeployedCapabilitiesRuntype = z.object({
    supplier: StackrateSupplierIdRuntype,
    tables: z.array(VersionedDecisionTableRuntype),
    supplierLqReference: SupplierLqReferenceRuntype,
});
export type DeployedCapabilities = z.infer<typeof DeployedCapabilitiesRuntype>;

// Request body of POST /price/capabilities/:supplier (deploy capability tables).
export const CapabilitiesDeploymentRuntype = z.object({
    supplier: PcbSupplierReferenceRuntype,
    tables: z.array(DecisionTableRuntype),
});
export type CapabilitiesDeployment = z.infer<typeof CapabilitiesDeploymentRuntype>;

// Also echoed inside CapabilityCheckResult responses, where `supplier` holds the canonical stackrate ID.
export const CapabilitiesDeploymentDescriptorRuntype = z.object({
    supplier: PcbSupplierReferenceRuntype,
    tables: z.array(DeployedDecisionRuntype),
});
export type CapabilitiesDeploymentDescriptor = z.infer<typeof CapabilitiesDeploymentDescriptorRuntype>;

export const PricingVariableRuntype = z.object({
    name: z.string(),
    varType: z.string(),
    label: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    options: z.array(z.string()).optional(),
});
export type PricingVariable = z.infer<typeof PricingVariableRuntype>;

// Calculation request of PUT .../calculate (assembly/version/specification path variants).
export const PriceRequestRuntype = z.object({
    count: z.number(),
    delivery: z.number(),
    nre: z.boolean().optional(),
    assembly: z.unknown().optional(),
    specification: z.string().optional(),
    panel: z.string().optional(),
});
export type PriceRequest = z.infer<typeof PriceRequestRuntype>;

// Calculation request of PUT /price/prices/calculate and the share variant: a PriceRequest plus
// the metadata that is otherwise resolved from the assembly (MetaInfo property bags — passed through).
export const ExtendedPriceRequestRuntype = z.object({
    request: PriceRequestRuntype,
    panel: z.unknown().optional(),
    pcb: z.unknown().optional(),
    specification: z.unknown().optional(),
    specificationUser: z.unknown().optional(),
    layerstack: z.unknown().optional(),
    customer: z.string().optional(),
});
export type ExtendedPriceRequest = z.infer<typeof ExtendedPriceRequestRuntype>;

export const PriceDetailRuntype = z.object({
    info: z.string().optional(),
    flex: z.number().optional(),
    fix: z.number().optional(),
    nre: z.number().optional(),
    perSqm: z.number().optional(),
    deliveryMethod: z.string().optional(),
    deliveryDays: z.number().optional(),
});
export type PriceDetail = z.infer<typeof PriceDetailRuntype>;

export const CalculatedPriceTableRuntype = z.object({
    name: z.string(),
    detail: z.array(PriceDetailRuntype),
});
export type CalculatedPriceTable = z.infer<typeof CalculatedPriceTableRuntype>;

export const StackratePricingFailedFieldRuntype = z.object({
    variable: z.string(),
    variableName: z.string(),
    value: z.string(),
    expression: z.string().optional(),
});
export type StackratePricingFailedField = z.infer<typeof StackratePricingFailedFieldRuntype>;

// The per-pricing calculation results form a sealed trait discriminated by `state`
// (Pricing.scala PricingResult: ok | error | break).
export const CalculatedPricingRuntype = z.object({
    state: z.literal('ok'),
    name: z.string(),
    supplier: StackrateSupplierIdRuntype,
    tables: z.array(CalculatedPriceTableRuntype),
    price: z.number(),
    unitPrice: z.number(),
    sumDetails: z.object({
        flexSum: z.number(),
        fixSum: z.number(),
        nreSum: z.number().optional(),
    }),
    currency: CurrencyRuntype,
});
export type CalculatedPricing = z.infer<typeof CalculatedPricingRuntype>;

export const PricingErrorRuntype = z.object({
    state: z.literal('error'),
    name: z.string(),
    supplier: StackrateSupplierIdRuntype,
    message: z.string(),
});
export type PricingError = z.infer<typeof PricingErrorRuntype>;

export const PricingBreakRuntype = z.object({
    state: z.literal('break'),
    name: z.string(),
    supplier: StackrateSupplierIdRuntype,
    message: z.string(),
    variableFailures: z.array(StackratePricingFailedFieldRuntype),
    hint: z.array(z.string()),
});
export type PricingBreak = z.infer<typeof PricingBreakRuntype>;

export const PricingResultRuntype = z.discriminatedUnion('state', [
    CalculatedPricingRuntype,
    PricingErrorRuntype,
    PricingBreakRuntype,
]);
export type PricingResult = z.infer<typeof PricingResultRuntype>;

export const CalculatedPricesRuntype = z.object({
    request: PriceRequestRuntype,
    pricings: z.array(PricingResultRuntype),
});
export type CalculatedPrices = z.infer<typeof CalculatedPricesRuntype>;

export const CapabilityCheckFailureRuntype = z.object({
    hint: z.string().optional(),
    variableFailures: z.array(StackratePricingFailedFieldRuntype),
});
export type CapabilityCheckFailure = z.infer<typeof CapabilityCheckFailureRuntype>;

export const CapabilityCheckResultRuntype = z.object({
    capability: CapabilitiesDeploymentDescriptorRuntype,
    failures: z.array(CapabilityCheckFailureRuntype),
});
export type CapabilityCheckResult = z.infer<typeof CapabilityCheckResultRuntype>;
